import { useEffect, useRef, useState } from 'react'
import axios, { type AxiosError } from 'axios'
import {
    type FetchStatus,
    type QueryStatus,
    useQuery,
    useQueryClient,
    useMutation,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { MutationOptions } from '@/types'
import { isErrorArrayResponse, isErrorResponse } from '@/helpers'
import {
    clearStoredReservationToken,
    getStoredReservationToken,
    setStoredReservationToken,
} from '@/session-storage'
import {
    isAdditionalPaymentCheckoutFlow,
    type CheckoutFlow,
} from '@/checkout-paths'
import type {
    ApplyReservationCouponMutationInput,
    BenefitCodePaymentPayload,
    ChangeReservationGarageMutationInput,
    ChangeReservationTimeMutationInput,
    CheckoutPaymentStatusPayload,
    CheckoutSuccessPageDataPayload,
    CheckoutUpdateReservationMutationPayload,
    CreateReservationMutationInput,
    CreateReservationMutationPayload,
    GetActiveReservationPayload,
    StripeCheckoutSessionPayload,
    SubscribeNewsletterMutationInput,
    SubscribeNewsletterMutationPayload,
    UpdateReservationContactInfoMutationInput,
    UpdateReservationSendSmsMutationInput,
    UpdateReservationSendSmsMutationPayload,
} from '@/types/reservation-checkout'
import type { StripeCheckoutUiMode } from '@/stripe-checkout'

export class CheckoutApiError extends Error {
    status?: number

    constructor(message: string, status?: number) {
        super(message)
        this.name = 'CheckoutApiError'
        this.status = status
    }
}

export type CheckoutViewStatus = 'loading' | 'expired' | 'error' | 'unavailable' | 'booked' | 'allocated'

type ActiveReservationQueryState = {
    status: QueryStatus
    fetchStatus?: FetchStatus
    data?: GetActiveReservationPayload
    error: Error | null
}

function isExpiredReservationError(error: unknown): error is CheckoutApiError {
    return error instanceof CheckoutApiError && error.status === 410
}

export function hasPaymentInProgress(data: GetActiveReservationPayload | undefined): boolean {
    return (
        data?.reservation.state === 'allocated'
        && 'payment_in_progress' in data.reservation
        && data.reservation.payment_in_progress
    )
}

export function getCheckoutViewStatus({
    query,
    persistedMissingTokenStatus,
}: {
    query: ActiveReservationQueryState,
    persistedMissingTokenStatus: Extract<CheckoutViewStatus, 'expired'> | null,
}): CheckoutViewStatus {
    if (query.status === 'pending') {
        if (query.fetchStatus === 'idle') {
            return persistedMissingTokenStatus ?? 'unavailable'
        }

        return 'loading'
    }

    if (query.status === 'error') {
        return isExpiredReservationError(query.error) ? 'expired' : 'error'
    }

    if (
        !!query.data
        && query.data.reservation.state === 'allocated'
        && query.data.garage !== null
        && 'checkout_amount_cents' in query.data.reservation
    ) {
        return 'allocated'
    }

    if (query.data?.reservation.state === 'booked') {
        return 'booked'
    }

    return query.data?.reservation.state === 'expired' ? 'expired' : 'unavailable'
}

function getReservationTokenHeaders(): { reservationToken: string, headers: { 'x-reservation-token': string } } {
    const reservationToken = getStoredReservationToken()

    if (!reservationToken) {
        throw new Error('Missing reservation token.')
    }

    return {
        reservationToken,
        headers: {
            'x-reservation-token': reservationToken,
        },
    }
}

function isGetActiveReservationPayload(data: unknown): data is GetActiveReservationPayload {
    if (typeof data !== 'object' || data === null || !isErrorArrayResponse(data)) {
        return false
    }

    const record = data as unknown as Record<string, unknown>

    return (
        'reservation' in record &&
        'garage' in record &&
        'currently_applied_coupon' in record &&
        'current_user' in record &&
        Array.isArray(record.available_benefit_codes) &&
        Array.isArray(record.current_user_cars)
    )
}

function setActiveReservationCache(
    queryClient: ReturnType<typeof useQueryClient>,
    data: unknown,
): void {
    const reservationToken = getStoredReservationToken()

    setActiveReservationCacheByToken(queryClient, reservationToken, data)
}

function setActiveReservationCacheByToken(
    queryClient: ReturnType<typeof useQueryClient>,
    reservationToken: string | null,
    data: unknown,
): void {
    if (!reservationToken || !isGetActiveReservationPayload(data)) {
        return
    }

    queryClient.setQueryData(['activeReservation', reservationToken], data)
}

export function setActiveReservationSendSmsCache(
    queryClient: ReturnType<typeof useQueryClient>,
    sendSms: boolean,
): void {
    const reservationToken = getStoredReservationToken()

    if (!reservationToken) {
        return
    }

    queryClient.setQueryData<GetActiveReservationPayload>(
        ['activeReservation', reservationToken],
        currentData => {
            if (!currentData || !('send_sms' in currentData.reservation)) {
                return currentData
            }

            return {
                ...currentData,
                reservation: {
                    ...currentData.reservation,
                    send_sms: sendSms,
                },
            }
        },
    )
}

export function setCheckoutSuccessNewsletterSubscriptionStatusCache(
    queryClient: ReturnType<typeof useQueryClient>,
): void {
    const reservationToken = getStoredReservationToken()

    if (!reservationToken) {
        return
    }

    queryClient.setQueriesData<CheckoutSuccessPageDataPayload>(
        {
            queryKey: ['checkoutSuccessPageData', reservationToken],
        },
        currentData => {
            if (!currentData) {
                return currentData
            }

            return {
                ...currentData,
                reservation: {
                    ...currentData.reservation,
                    newsletter_subscription_status: 'subscribed',
                },
            }
        },
    )
}

function getCheckoutApiErrorMessage(error: unknown): string {
    if (error instanceof Error && !('response' in error)) {
        return error.message
    }

    const axiosResponse = error as AxiosError
    const errorData = axiosResponse.response?.data

    if (isErrorArrayResponse(errorData) && errorData.errors.length > 0) {
        return errorData.errors.join(', ')
    }

    if (isErrorResponse(errorData)) {
        return errorData.error
    }

    if (error instanceof Error && error.message) {
        return error.message
    }

    console.error(error)
    return 'Došlo k neočekávané chybě.'
}

function handleCheckoutApiError(error: unknown): CheckoutApiError {
    if (error instanceof CheckoutApiError) {
        return error
    }

    const axiosResponse = error as AxiosError
    const errorData = axiosResponse.response?.data
    const status = axiosResponse.response?.status

    if (status === 404 || status === 410) {
        clearStoredReservationToken()
    }

    return new CheckoutApiError(
        getCheckoutApiErrorMessage(errorData ? axiosResponse : error),
        status,
    )
}

export async function fetchActiveReservationByToken(
    reservationToken: string,
): Promise<GetActiveReservationPayload> {
    try {
        const { data }: { data: GetActiveReservationPayload } = await axios.get(
            '/api/reservation_checkout/get_active_reservation',
            {
                headers: {
                    'x-reservation-token': reservationToken,
                },
            },
        )

        return data
    } catch (error) {
        throw handleCheckoutApiError(error)
    }
}

export async function resolvePaymentsInProgressByToken(
    reservationToken: string,
): Promise<GetActiveReservationPayload> {
    try {
        const { data }: { data: GetActiveReservationPayload } = await axios.post(
            '/api/reservation_checkout/resolve_payments_in_progress',
            undefined,
            {
                headers: {
                    'x-reservation-token': reservationToken,
                },
            },
        )

        return data
    } catch (error) {
        throw handleCheckoutApiError(error)
    }
}

export const useCreateReservation = (options: MutationOptions) => {
    const queryClient = useQueryClient()
    const { i18n } = useTranslation()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (input: CreateReservationMutationInput) => {
            const payload = {
                begins_at: input.begins_at,
                ends_at: input.ends_at,
                garage_id: input.garage_id.toString(),
                locale: i18n.language,
            }

            return axios.post<CreateReservationMutationPayload>(
                '/api/reservation_checkout/create_reservation',
                payload,
            )
        },
        onSuccess: response => {
            if (response.data.reservation_token) {
                setStoredReservationToken(response.data.reservation_token)
            }
            queryClient.invalidateQueries({ queryKey: ['activeReservation'] })
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorArrayResponse(errorData) && errorData.errors.length > 0) {
                    onErrorCallback(errorData.errors.join(', '))
                } else if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.')
                }
            }
        }
    })
}

type PaymentResolutionState = {
    reservationToken: string
    isResolving: boolean
    error: Error | null
}

function useResolvePaymentsInProgress(
    reservationToken: string | null,
    shouldResolve: boolean,
): {
    isBlocking: boolean
    error: Error | null
} {
    const queryClient = useQueryClient()
    const startedReservationTokensRef = useRef(new Set<string>())
    const currentReservationTokenRef = useRef(reservationToken)
    const isMountedRef = useRef(false)
    const [state, setState] = useState<PaymentResolutionState | null>(null)

    currentReservationTokenRef.current = reservationToken

    useEffect(() => {
        isMountedRef.current = true

        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (
            !shouldResolve
            || !reservationToken
            || startedReservationTokensRef.current.has(reservationToken)
        ) {
            return
        }

        startedReservationTokensRef.current.add(reservationToken)
        setState({
            reservationToken,
            isResolving: true,
            error: null,
        })

        void resolvePaymentsInProgressByToken(reservationToken).then(data => {
            if (
                !isMountedRef.current
                || currentReservationTokenRef.current !== reservationToken
            ) {
                return
            }

            setActiveReservationCacheByToken(queryClient, reservationToken, data)
            setState({
                reservationToken,
                isResolving: false,
                error: null,
            })
        }).catch(error => {
            if (
                !isMountedRef.current
                || currentReservationTokenRef.current !== reservationToken
            ) {
                return
            }

            setState({
                reservationToken,
                isResolving: false,
                error: error instanceof Error ? error : new Error(String(error)),
            })
        })
    }, [queryClient, reservationToken, shouldResolve])

    const isCurrentReservation = state?.reservationToken === reservationToken
    const error = (
        state?.error
        && (
            isCurrentReservation
            || (
                reservationToken === null
                && state.error instanceof CheckoutApiError
                && (state.error.status === 404 || state.error.status === 410)
            )
        )
    ) ? state.error : null
    const isStarting = (
        shouldResolve
        && !!reservationToken
        && !startedReservationTokensRef.current.has(reservationToken)
    )

    return {
        error,
        isBlocking: isStarting || (
            isCurrentReservation
            && state?.isResolving === true
        ),
    }
}

export function useGetActiveReservationAndCheckoutViewStatus(options?: { enabled?: boolean }) {
    const reservationToken = getStoredReservationToken()
    const enabled = options?.enabled ?? true
    const [persistedMissingTokenStatus, setPersistedMissingTokenStatus] = useState<Extract<CheckoutViewStatus, 'expired'> | null>(null)

    const query = useQuery({
        queryKey: ['activeReservation', reservationToken],
        queryFn: async () => {
            if (!reservationToken) {
                throw new Error('Missing reservation token.')
            }

            return fetchActiveReservationByToken(reservationToken)
        },
        enabled: enabled && !!reservationToken,
    })

    const paymentResolution = useResolvePaymentsInProgress(
        reservationToken,
        enabled
        && query.status === 'success'
        && hasPaymentInProgress(query.data),
    )

    useEffect(() => {
        if (
            (query.status === 'error' && isExpiredReservationError(query.error))
            || isExpiredReservationError(paymentResolution.error)
        ) {
            setPersistedMissingTokenStatus('expired')
            return
        }

        if (reservationToken) {
            setPersistedMissingTokenStatus(null)
        }
    }, [paymentResolution.error, query.error, query.status, reservationToken])

    const effectiveQueryStatus = paymentResolution.error ? 'error' : query.status
    const effectiveQueryError = paymentResolution.error ?? query.error

    return {
        ...query,
        status: effectiveQueryStatus,
        error: effectiveQueryError,
        viewStatus: getCheckoutViewStatus({
            query: {
                status: effectiveQueryStatus,
                fetchStatus: query.fetchStatus,
                data: query.data,
                error: effectiveQueryError,
            },
            persistedMissingTokenStatus,
        }),
        isPaymentResolutionBlocking: paymentResolution.isBlocking,
    }
}

export const useChangeReservationTime = (options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (input: ChangeReservationTimeMutationInput) => {
            const { headers } = getReservationTokenHeaders()
            const payload = input

            return axios.patch<CheckoutUpdateReservationMutationPayload>(
                '/api/reservation_checkout/change_reservation_time',
                payload,
                { headers },
            )
        },
        onSuccess: response => {
            setActiveReservationCache(queryClient, response.data)

            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data

            setActiveReservationCache(queryClient, errorData)

            if (onErrorCallback) {
                if (isErrorArrayResponse(errorData) && errorData.errors.length > 0) {
                    onErrorCallback(errorData.errors.join(', '))
                } else if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.')
                }
            }
        },
    })
}

export const useChangeReservationGarage = (options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (input: ChangeReservationGarageMutationInput) => {
            const { headers } = getReservationTokenHeaders()
            const payload = {
                ...input,
                garage_id: input.garage_id.toString(),
            }

            return axios.patch<CheckoutUpdateReservationMutationPayload>(
                '/api/reservation_checkout/change_reservation_garage',
                payload,
                { headers },
            )
        },
        onSuccess: response => {
            setActiveReservationCache(queryClient, response.data)

            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data

            setActiveReservationCache(queryClient, errorData)

            if (onErrorCallback) {
                if (isErrorArrayResponse(errorData) && errorData.errors.length > 0) {
                    onErrorCallback(errorData.errors.join(', '))
                } else if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.')
                }
            }
        },
    })
}

export const useUpdateReservationContactInfo = (options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (input: UpdateReservationContactInfoMutationInput) => {
            const { headers } = getReservationTokenHeaders()
            const payload: UpdateReservationContactInfoMutationInput = {}

            if (input.name !== undefined) {
                payload.name = input.name
            }

            if (input.email !== undefined) {
                payload.email = input.email
            }

            if (input.phone_number !== undefined) {
                payload.phone_number = input.phone_number
            }

            if (input.phone_number_2 !== undefined) {
                payload.phone_number_2 = input.phone_number_2
            }

            if (input.number_plate !== undefined) {
                payload.number_plate = input.number_plate
            }

            if (input.car_color !== undefined) {
                payload.car_color = input.car_color
            }

            if (input.locale !== undefined) {
                payload.locale = input.locale
            }

            if (input.invoice_city !== undefined) {
                payload.invoice_city = input.invoice_city
            }

            if (input.invoice_country !== undefined) {
                payload.invoice_country = input.invoice_country
            }

            if (input.invoice_dic !== undefined) {
                payload.invoice_dic = input.invoice_dic
            }

            if (input.invoice_ic !== undefined) {
                payload.invoice_ic = input.invoice_ic
            }

            if (input.invoice_street !== undefined) {
                payload.invoice_street = input.invoice_street
            }

            if (input.invoice_title !== undefined) {
                payload.invoice_title = input.invoice_title
            }

            if (input.invoice_zip_code !== undefined) {
                payload.invoice_zip_code = input.invoice_zip_code
            }

            if (input.requested_invoice !== undefined) {
                payload.requested_invoice = input.requested_invoice
            }

            return axios.patch<CheckoutUpdateReservationMutationPayload>(
                '/api/reservation_checkout/update_reservation_contact_info',
                payload,
                { headers },
            )
        },
        onSuccess: response => {
            setActiveReservationCache(queryClient, response.data)

            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data

            setActiveReservationCache(queryClient, errorData)

            if (onErrorCallback) {
                if (isErrorArrayResponse(errorData) && errorData.errors.length > 0) {
                    onErrorCallback(errorData.errors.join(', '))
                } else if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.')
                }
            }
        },
    })
}

export async function updateReservationSendSms(
    input: UpdateReservationSendSmsMutationInput,
): Promise<UpdateReservationSendSmsMutationPayload> {
    const { headers } = getReservationTokenHeaders()
    const response = await axios.patch<UpdateReservationSendSmsMutationPayload>(
        '/api/reservation_checkout/update_reservation_send_sms',
        input,
        { headers },
    )

    return response.data
}

export const useUpdateReservationSendSms = (options: MutationOptions) => {
    const queryClient = useQueryClient()
    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: updateReservationSendSms,
        onSuccess: response => {
            setActiveReservationSendSmsCache(queryClient, response.send_sms)
            onSuccessCallback?.(response)
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            onErrorCallback?.(getCheckoutApiErrorMessage(axiosResponse))
        },
    })
}

export const useApplyReservationCoupon = (options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (input: ApplyReservationCouponMutationInput) => {
            const { headers } = getReservationTokenHeaders()
            const payload = {
                coupon_code: input.coupon_code,
            }

            return axios.patch<CheckoutUpdateReservationMutationPayload>(
                '/api/reservation_checkout/apply_coupon_on_reservation',
                payload,
                { headers },
            )
        },
        onSuccess: response => {
            setActiveReservationCache(queryClient, response.data)

            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data

            setActiveReservationCache(queryClient, errorData)

            if (onErrorCallback) {
                if (isErrorArrayResponse(errorData) && errorData.errors.length > 0) {
                    onErrorCallback(errorData.errors.join(', '))
                } else if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.')
                }
            }
        },
    })
}

export const useRemoveReservationCoupon = (options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: () => {
            const { headers } = getReservationTokenHeaders()

            return axios.patch<CheckoutUpdateReservationMutationPayload>(
                '/api/reservation_checkout/remove_coupon_from_reservation',
                {},
                { headers },
            )
        },
        onSuccess: response => {
            setActiveReservationCache(queryClient, response.data)

            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data

            setActiveReservationCache(queryClient, errorData)

            if (onErrorCallback) {
                if (isErrorArrayResponse(errorData) && errorData.errors.length > 0) {
                    onErrorCallback(errorData.errors.join(', '))
                } else if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.')
                }
            }
        },
    })
}

export const useCreateStripeCheckoutSession = (options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (input?: CreateStripeCheckoutSessionInput) => createStripeCheckoutSession(input),
        onSuccess: response => {
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: error => {
            const axiosResponse = error as AxiosError

            if (axiosResponse.response?.status === 410) {
                queryClient.invalidateQueries({ queryKey: ['activeReservation'] })
            }

            if (onErrorCallback) {
                onErrorCallback(getCheckoutApiErrorMessage(error))
            }
        },
    })
}

export interface CreateStripeCheckoutSessionInput {
    checkoutUiMode?: StripeCheckoutUiMode
    checkoutFlow?: CheckoutFlow
    returnTo?: string | null
}

export async function createStripeCheckoutSession(
    input: CreateStripeCheckoutSessionInput = {},
) {
    const {
        checkoutUiMode = 'embedded',
        checkoutFlow = 'reservation',
        returnTo,
    } = input
    const { headers } = getReservationTokenHeaders()
    const endpoints: Record<CheckoutFlow, string> = {
        reservation: '/api/reservation_checkout/pay_by_stripe',
        prolongation: '/api/reservation_checkout/pay_prolong_by_stripe',
        change_unit: '/api/reservation_checkout/pay_change_unit_by_stripe',
    }
    const endpoint = endpoints[checkoutFlow]
    const payload: Record<string, string> = {
        checkout_ui_mode: checkoutUiMode,
    }

    if (isAdditionalPaymentCheckoutFlow(checkoutFlow) && returnTo) {
        payload.return_to = returnTo
    }

    return axios.post<StripeCheckoutSessionPayload>(
        endpoint,
        payload,
        { headers },
    )
}

export async function payReservationByBenefitCode(
    benefitCode: string,
): Promise<BenefitCodePaymentPayload> {
    const { headers } = getReservationTokenHeaders()
    const payload = {
        benefit_code: benefitCode,
    }
    const { data }: { data: BenefitCodePaymentPayload } = await axios.post(
        '/api/reservation_checkout/pay_by_benefit_code',
        payload,
        { headers },
    )

    return data
}

export const usePayByBenefitCode = (options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (benefitCode: string) => payReservationByBenefitCode(benefitCode),
        onSuccess: response => {
            if (onSuccessCallback) {
                onSuccessCallback(response)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: error => {
            const axiosResponse = error as AxiosError

            if (axiosResponse.response?.status === 410) {
                queryClient.invalidateQueries({ queryKey: ['activeReservation'] })
            }

            if (onErrorCallback) {
                onErrorCallback(getCheckoutApiErrorMessage(error))
            }
        },
    })
}

export async function fetchCheckoutSuccessPageData(
    paymentId: string | null,
): Promise<CheckoutSuccessPageDataPayload> {
    const reservationToken = getStoredReservationToken()

    if (!reservationToken) {
        throw new Error('Missing reservation token.')
    }

    if (!paymentId) {
        throw new Error('Missing payment id.')
    }

    try {
        const { data }: { data: CheckoutSuccessPageDataPayload } = await axios.get(
            '/api/reservation_checkout/get_success_page_data',
            {
                params: {
                    payment_id: paymentId,
                },
                headers: {
                    'x-reservation-token': reservationToken,
                },
            },
        )

        return data
    } catch (error) {
        const axiosResponse = error as AxiosError

        if (axiosResponse.response?.status === 404) {
            clearStoredReservationToken()
        }

        throw new Error(getCheckoutApiErrorMessage(error))
    }
}

export function useGetCheckoutSuccessPageData(
    paymentId: string | null,
    options?: { enabled?: boolean },
) {
    const reservationToken = getStoredReservationToken()
    const enabled = options?.enabled ?? true

    return useQuery({
        queryKey: ['checkoutSuccessPageData', reservationToken, paymentId],
        queryFn: async () => fetchCheckoutSuccessPageData(paymentId),
        enabled: enabled && !!reservationToken && !!paymentId,
    })
}

export async function subscribeReservationToNewsletter(
    input: SubscribeNewsletterMutationInput,
): Promise<SubscribeNewsletterMutationPayload> {
    const { headers } = getReservationTokenHeaders()
    const { data } = await axios.post<SubscribeNewsletterMutationPayload>(
        '/api/reservation_checkout/subscribe_newsletter',
        input,
        { headers },
    )

    return data
}

export function useSubscribeReservationToNewsletter(onSubscribed?: () => void) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: subscribeReservationToNewsletter,
        onSuccess: data => {
            if (data.subscribed) {
                onSubscribed?.()
                setCheckoutSuccessNewsletterSubscriptionStatusCache(queryClient)
            }
        },
    })
}

export function useGetCheckoutPaymentStatus(
    paymentId: string | null,
    options?: { enabled?: boolean },
) {
    const reservationToken = getStoredReservationToken()
    const enabled = options?.enabled ?? true

    return useQuery({
        queryKey: ['checkoutPaymentStatus', reservationToken, paymentId],
        queryFn: async () => {
            if (!reservationToken) {
                throw new Error('Missing reservation token.')
            }

            if (!paymentId) {
                throw new Error('Missing payment id.')
            }

            try {
                const { data }: { data: CheckoutPaymentStatusPayload } = await axios.get(
                    '/api/reservation_checkout/get_payment_status',
                    {
                        params: {
                            payment_id: paymentId,
                        },
                        headers: {
                            'x-reservation-token': reservationToken,
                        },
                    },
                )

                return data
            } catch (error) {
                const axiosResponse = error as AxiosError

                if (axiosResponse.response?.status === 404) {
                    clearStoredReservationToken()
                }

                throw new Error(getCheckoutApiErrorMessage(error))
            }
        },
        enabled: enabled && !!reservationToken && !!paymentId,
        refetchInterval: query => {
            const data = query.state.data as CheckoutPaymentStatusPayload | undefined
            return data?.status === 'created' ? 3000 : false
        },
    })
}
